"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { Search, ArrowRight, Instagram, Facebook, Twitter, Linkedin, Star } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const SERVICE_CATEGORIES = [{
  id: "top",
  label: "Luxury Hotels",
  color: "bg-primary text-primary-foreground"
}, {
  id: "book",
  label: "Flight Deals",
  color: "bg-secondary text-secondary-foreground"
}, {
  id: "new",
  label: "Taxi Services",
  color: "bg-accent text-accent-foreground"
}, {
  id: "all",
  label: "All Bookings",
  color: "bg-muted text-muted-foreground"
}];
const SERVICES = [{
  id: 1,
  title: "Hotel Bookings",
  description: "Access a global network of premium accommodations with real-time availability.",
  count: "500k+ hotels",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645649/img/hotel_booking_view.png"
}, {
  id: 2,
  title: "Flight Search",
  description: "Compare thousands of flights across major airlines to find the best routes.",
  count: "200+ airlines",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645649/img/flight_search_sky.png"
}, {
  id: 3,
  title: "Airport Taxis",
  description: "Reliable door-to-door transportation services in over 100 major cities.",
  count: "1k+ routes",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645649/img/taxi_service_city.png"
}];
const FOOTER_LINKS = {
  services: [{
    label: "Hotel Search",
    href: "/AccommodationsPage"
  }, {
    label: "Flight Booking",
    href: "/FlightsPage"
  }, {
    label: "Taxi Transfers",
    href: "#"
  }, {
    label: "Holiday Rentals",
    href: "/AccommodationsPage"
  }, {
    label: "Business Travel",
    href: "#"
  }, {
    label: "Group Bookings",
    href: "#"
  }],
  company: [{
    label: "About Us",
    href: "#"
  }, {
    label: "Contact Us",
    href: "/ContactPage"
  }, {
    label: "Partner with Us",
    href: "#"
  }]
};
function HeroSection() {
  return <div data-section-id="224119" className="relative min-h-[80vh] flex items-center justify-center bg-muted overflow-hidden">
      {}
      <div className="absolute inset-0 z-0">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645649/img/travel_hero_sun_drenched.png" alt="Sun-drenched travel destination" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/60 to-secondary/20" />
      </div>

      {}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-24 md:py-32 flex flex-col items-start">
        <Badge variant="outline" className="text-primary-foreground border-primary-foreground/30 mb-6 uppercase tracking-widest text-xs bg-transparent">
          Global Travel Discovery Engine
        </Badge>
        
        <h1 className="font-serif text-5xl md:text-7xl font-bold text-primary-foreground leading-tight mb-6 max-w-4xl">
          Discover Your <span className="italic font-light">Next Escape</span> with Ease
        </h1>
        
        <p className="text-lg md:text-xl text-primary-foreground/90 max-w-2xl mb-10 font-default">
          Seamlessly book hotels, flights, and transfers through our integrated global search engine.
        </p>
        
        <div className="flex flex-wrap gap-4 mb-16">
          <Button size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-8 shadow-lg">
            Start Booking
          </Button>
          <Button size="lg" variant="outline" className="rounded-full bg-background text-foreground hover:bg-background/90 px-8 border-none shadow-md">
            View Deals
          </Button>
        </div>

        {}
        <div className="flex items-center gap-4">
          <div className="flex -space-x-3">
            {[1, 2, 3, 4].map(i => <SafeHydrate><Avatar key={i} data-index={i} className="border-2 border-primary w-10 h-10">
                <AvatarImage src={`/img/traveler_avatar_${i}.png`} />
                <AvatarFallback>T{i}</AvatarFallback>
              </Avatar></SafeHydrate>)}
          </div>
          <div className="text-sm text-primary-foreground/80 font-medium">
            Trusted by 50,000+ travelers
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
        <svg className="relative block w-full h-[50px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C59.71,118.08,130.83,121.32,201.2,112.5,242.49,107.36,282.8,92.2,321.39,56.44Z" className="fill-background"></path>
        </svg>
      </div>
    </div>;
}
function ServicesSection() {
  return <div className="py-24 px-6 max-w-7xl mx-auto bg-background">
      <div className="text-center mb-16">
        <div className="flex items-center justify-center gap-4 mb-4">
          <Star className="w-6 h-6 text-primary" />
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground">Travel Services</h2>
          <Star className="w-6 h-6 text-primary" />
        </div>
        <p className="text-muted-foreground font-default max-w-2xl mx-auto mb-8">
          Explore our comprehensive booking engine for all your travel needs, from luxury stays to quick transfers.
        </p>
        
        <div className="max-w-md mx-auto relative mb-12">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input placeholder="Search destinations or hotels..." className="pl-12 rounded-full h-12 bg-background border-border shadow-sm" />
        </div>

        <div className="flex flex-wrap justify-center gap-3">
          {SERVICE_CATEGORIES.map((cat, i) => <Button key={cat.id} data-index={i} variant="outline" className={`rounded-full border-none shadow-sm ${cat.color}`}>
              {cat.label}
            </Button>)}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {SERVICES.map((service, i) => <Card key={service.id} data-index={i} className="overflow-hidden border-border shadow-md rounded-2xl group hover:shadow-lg transition-shadow">
            <div className="relative h-48 bg-muted overflow-hidden">
              <img src={service.image} alt={service.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <Badge className="absolute top-4 right-4 bg-primary text-primary-foreground rounded-full px-3 py-1">
                {service.count}
              </Badge>
            </div>
            <CardContent className="p-6">
              <h3 className="font-serif text-xl font-semibold text-foreground mb-2">{service.title}</h3>
              <p className="text-muted-foreground text-sm mb-6 font-default line-clamp-2">
                {service.description}
              </p>
              <Button variant="outline" className="w-full rounded-full border-border text-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                Search Now <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>)}
      </div>
    </div>;
}
function ProductsGridConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-[400px] rounded-2xl bg-muted animate-pulse" />)}
      </div>;
  }
  if (error || products.length === 0) {
    return <div className="text-center py-12 text-muted-foreground">
        No travel packages found at the moment.
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {products.map((product, i) => <Card key={product.id} data-index={i} className="overflow-hidden border-border shadow-md rounded-2xl group hover:shadow-lg transition-shadow flex flex-col">
          <Link to={product.link} className="relative h-64 bg-muted overflow-hidden block shrink-0">
            {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full flex items-center justify-center bg-secondary/20 text-secondary">
                No Image
              </div>}
            <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground rounded-full px-3 py-1">
              Top Rated
            </Badge>
          </Link>
          <CardContent className="p-6 flex flex-col flex-grow">
            <Link to={product.link}>
              <h3 className="font-serif text-xl font-semibold text-foreground mb-2 hover:text-primary transition-colors line-clamp-1">
                {product.title}
              </h3>
            </Link>
            {product.short_description && <div className="text-muted-foreground text-sm mb-4 font-default line-clamp-2" dangerouslySetInnerHTML={{
          __html: product.short_description
        }} />}
            <div className="mt-auto pt-4 flex items-center justify-between">
              <span className="font-default font-semibold text-lg text-foreground">
                ${product.price?.toFixed(2) || '0.00'}
              </span>
              <div className="w-32">
                <AddToCartButton productId={product.id} className="w-full" buttonClassName="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm" text="Book Now" showQuantity={false} />
              </div>
            </div>
          </CardContent>
        </Card>)}
    </div>;
}
function ProductsSection() {
  const wp_query = new WP_Query({
    per_page: 3
  });
  return <div className="py-24 px-6 max-w-7xl mx-auto bg-background">
      <div className="text-center mb-16">
        <div className="flex items-center justify-center gap-4 mb-4">
          <Star className="w-6 h-6 text-primary" />
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground">Featured Destinations</h2>
          <Star className="w-6 h-6 text-primary" />
        </div>
        <p className="text-muted-foreground font-default max-w-2xl mx-auto mb-8">
          Hand-picked travel packages and exclusive hotel deals curated for your next adventure.
        </p>
        
        <div className="max-w-md mx-auto relative mb-12">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input placeholder="Search packages..." className="pl-12 rounded-full h-12 bg-background border-border shadow-sm" />
        </div>
      </div>

      <EcommerceProductsProvider wp_query={{
      per_page: 3
    }}>
        <ProductsGridConsumer />
      </EcommerceProductsProvider>
      
      <div className="mt-12 text-center">
        <Button variant="outline" className="rounded-full border-border text-foreground px-8 hover:bg-muted shadow-sm">
          View All Offers
        </Button>
      </div>
    </div>;
}
function CTASection() {
  return <div className="bg-secondary py-24 px-6">
      <div className="max-w-4xl mx-auto text-center">
        <Badge className="bg-primary/20 text-primary border-none mb-6 tracking-widest uppercase text-xs">
          Partner Program
        </Badge>
        <h2 className="font-serif text-4xl md:text-5xl font-bold text-secondary-foreground mb-6">
          List Your Property or Service
        </h2>
        <p className="text-lg text-secondary-foreground/80 font-default max-w-2xl mx-auto mb-10">
          Join our global network and reach millions of travelers. Integrate your booking engine with our platform for organic growth.
        </p>
        <Button size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-10 h-14 text-lg shadow-xl">
          Become a Partner
        </Button>
      </div>
    </div>;
}
function FooterSection() {
  return <footer className="bg-secondary pt-20 pb-10 px-6 border-t border-secondary-foreground/10">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          {}
          <div className="md:col-span-4">
            <Link to="/" className="inline-block mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                  <span className="font-serif font-bold text-primary-foreground text-xl">T</span>
                </div>
                <span className="font-serif font-bold text-xl text-secondary-foreground tracking-wide">
                  TRAVEL <span className="font-normal">DISCOVERY</span>
                </span>
              </div>
            </Link>
            <p className="text-secondary-foreground/70 font-default text-sm leading-relaxed mb-8 pr-4">
              Your ultimate gateway to global travel. We provide a seamless white-label search experience for hotels, flights, and transportation.
            </p>
            <div className="flex gap-4">
              <Link to="#" className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground hover:bg-primary/80 transition-colors">
                <Instagram className="w-5 h-5" />
              </Link>
              <Link to="#" className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground hover:bg-primary/80 transition-colors">
                <Facebook className="w-5 h-5" />
              </Link>
              <Link to="#" className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground hover:bg-primary/80 transition-colors">
                <Twitter className="w-5 h-5" />
              </Link>
              <Link to="#" className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground hover:bg-primary/80 transition-colors">
                <Linkedin className="w-5 h-5" />
              </Link>
            </div>
          </div>

          {}
          <div className="hidden md:block md:col-span-2" />

          {}
          <div className="md:col-span-3">
            <h4 className="font-default font-semibold text-secondary-foreground tracking-wider uppercase mb-6 text-sm">
              Bookings
            </h4>
            <ul className="space-y-4">
              {FOOTER_LINKS.services.map((link, i) => <li key={i} data-index={i}>
                  <Link to={link.href} className="text-secondary-foreground/70 hover:text-primary transition-colors text-sm font-default">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-default font-semibold text-secondary-foreground tracking-wider uppercase mb-6 text-sm">
              Company
            </h4>
            <ul className="space-y-4">
              {FOOTER_LINKS.company.map((link, i) => <li key={i} data-index={i}>
                  <Link to={link.href} className="text-secondary-foreground/70 hover:text-primary transition-colors text-sm font-default">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </div>
        </div>

        <Separator className="bg-secondary-foreground/10 mb-8" />
        
        <div className="text-center md:text-left text-secondary-foreground/50 text-xs font-default flex flex-col md:flex-row justify-between items-center">
          <p>© {new Date().getFullYear()} TRAVEL Discovery. All rights reserved.</p>
          <div className="flex gap-4 mt-4 md:mt-0">
            <Link to="#" className="hover:text-secondary-foreground transition-colors">Privacy Policy</Link>
            <Link to="#" className="hover:text-secondary-foreground transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>;
}
export default function LandingPage() {
  return <section id="traveldiscovery" className="bg-background text-foreground w-full overflow-x-hidden">
      <HeroSection />
      <ServicesSection />
      <ProductsSection />
      <CTASection />
      <FooterSection />
    </section>;
}